/**
 * Reservation Payment Result - Shows result after Mercado Pago payment
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';

export default function ReservationPaymentResult() {
  const { clubId, status, reservationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReservation();
  }, [reservationId]); // fetchReservation is defined below

  const fetchReservation = async () => {
    try {
      setLoading(true);
      // First try to get by confirmation code, then by ID
      const res = await api.get(`/reservations/public/reservation/${reservationId}`);
      setReservation(res.data.reservation);
    } catch (err) {
      console.error('Error fetching reservation:', err);
      setError('No se pudo cargar la información de la reservación');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const getStatusConfig = () => {
    switch (status) {
      case 'confirmado':
        return {
          icon: Check,
          iconBg: 'bg-green-500/20',
          iconColor: 'text-green-500',
          title: '¡Pago Exitoso!',
          subtitle: 'Tu reservación ha sido confirmada',
          showCode: true,
        };
      case 'pendiente':
        return {
          icon: Clock,
          iconBg: 'bg-yellow-500/20',
          iconColor: 'text-yellow-500',
          title: 'Pago Pendiente',
          subtitle: 'Tu pago está siendo procesado. Te notificaremos cuando se confirme.',
          showCode: true,
        };
      case 'error':
        return {
          icon: X,
          iconBg: 'bg-red-500/20',
          iconColor: 'text-red-500',
          title: 'Pago No Completado',
          subtitle: 'Hubo un problema con tu pago. Puedes intentar nuevamente.',
          showCode: false,
        };
      default:
        return {
          icon: AlertCircle,
          iconBg: 'bg-gray-500/20',
          iconColor: 'text-gray-500',
          title: 'Estado Desconocido',
          subtitle: 'Por favor verifica tu reservación.',
          showCode: false,
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl p-6 text-center"
      >
        <div className={`w-20 h-20 rounded-full ${config.iconBg} flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-10 h-10 ${config.iconColor}`} />
        </div>
        
        <h1 className="font-display font-bold text-2xl mb-2">{config.title}</h1>
        <p className="text-muted-foreground mb-6">{config.subtitle}</p>
        
        {config.showCode && reservation?.confirmation_code && (
          <div className="bg-primary/10 border-2 border-primary/30 rounded-xl p-4 mb-6">
            <p className="text-sm text-muted-foreground mb-1">Tu código de confirmación</p>
            <p className="text-4xl font-display font-bold tracking-[0.3em] text-primary">
              {reservation.confirmation_code}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              📱 Muestra este código al llegar al club
            </p>
          </div>
        )}
        
        {reservation && (
          <div className="text-left space-y-2 mb-6 text-sm bg-secondary/50 rounded-lg p-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cancha</span>
              <span className="font-medium">{reservation.court_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fecha</span>
              <span className="font-medium">
                {new Date(reservation.date + 'T12:00:00').toLocaleDateString('es-MX', { 
                  weekday: 'long', day: 'numeric', month: 'long' 
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Horario</span>
              <span className="font-medium">{reservation.start_time} - {reservation.end_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="font-bold text-primary">${reservation.price?.toFixed(2)} MXN</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 text-destructive rounded-lg p-4 mb-6 text-sm">
            {error}
          </div>
        )}
        
        <div className="space-y-3">
          {status === 'error' && (
            <Button 
              className="w-full" 
              onClick={() => navigate(`/reservar/${clubId}`)}
            >
              Intentar Nueva Reservación
            </Button>
          )}
          <Button 
            variant={status === 'error' ? 'outline' : 'default'} 
            className="w-full"
            onClick={() => navigate('/')}
          >
            Ir al Inicio
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
